import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, ApiUnreachableError, type ApiClient } from "../api/client.js";
import type { NewPlanComment, PlanCommentPatch, PlanCommentThread } from "../api/types.js";

/**
 * Comments pinned to the plan (migration 0043), held for the drawing and the
 * Comments panel and written through to the API.
 *
 * NOT OPTIMISTIC, unlike the plan's objects. A comment is a sentence somebody
 * typed; showing it posted before the server has it — and then taking it
 * away — would lose words, not a dragged radiator's position. So each write
 * waits for the answer, the panel says it is posting, and a refusal leaves the
 * text where it was typed, with the reason.
 *
 * Every write answers the whole thread, which replaces the one held.
 */

/** What the plan needs from wherever comments are kept. The API, in the portal. */
export interface PlanCommentsSource {
  /** Null: this server does not keep comments (an API older than the route). */
  list(): Promise<readonly PlanCommentThread[] | null>;
  create(input: NewPlanComment): Promise<PlanCommentThread>;
  reply(threadId: string, body: string): Promise<PlanCommentThread>;
  update(id: string, patch: PlanCommentPatch): Promise<PlanCommentThread>;
  remove(id: string): Promise<void>;
}

export function planCommentsFromApi(client: ApiClient, surveyId: string): PlanCommentsSource {
  return {
    list: () => client.planComments(surveyId),
    create: (input) => client.createPlanComment(surveyId, input),
    reply: (threadId, body) => client.replyToPlanComment(surveyId, threadId, body),
    update: (id, patch) => client.updatePlanComment(surveyId, id, patch),
    remove: (id) => client.deletePlanComment(surveyId, id),
  };
}

/** Who is looking: what the panel offers them. The API decides again on every write. */
export interface PlanCommentViewer {
  readonly userId: string;
  /** May comment, reply, resolve and reopen (`design: read` and `chat: create`). */
  readonly canComment: boolean;
  /** May delete anyone's comment (`chat: delete` — owner, admin). */
  readonly canModerate: boolean;
}

export type PlanCommentsStatus = "unavailable" | "unsupported" | "loading" | "ready" | "failed";

export interface PlanCommentsState {
  readonly status: PlanCommentsStatus;
  readonly threads: readonly PlanCommentThread[];
  readonly loadError: string | null;
  /** Why the last write was refused, in words, until dismissed or superseded. */
  readonly error: string | null;
  /** A write is in flight. */
  readonly busy: boolean;
  /** Resolves to the thread as saved, or null if it was refused (the reason is in `error`). */
  readonly create: (input: NewPlanComment) => Promise<PlanCommentThread | null>;
  readonly reply: (threadId: string, body: string) => Promise<PlanCommentThread | null>;
  readonly update: (id: string, patch: PlanCommentPatch) => Promise<PlanCommentThread | null>;
  readonly remove: (id: string) => Promise<boolean>;
  readonly dismissError: () => void;
  readonly reload: () => void;
}

function reasonOf(error: unknown): string {
  if (error instanceof ApiError || error instanceof ApiUnreachableError) return error.message;
  return "Something went wrong saving it.";
}

function upsert(threads: readonly PlanCommentThread[], thread: PlanCommentThread): PlanCommentThread[] {
  const next = threads.some((t) => t.id === thread.id)
    ? threads.map((t) => (t.id === thread.id ? thread : t))
    : [...threads, thread];
  return next.sort((a, b) => a.number - b.number);
}

export function usePlanComments(source: PlanCommentsSource | undefined, surveyId: string): PlanCommentsState {
  const [threads, setThreads] = useState<readonly PlanCommentThread[]>([]);
  const [status, setStatus] = useState<PlanCommentsStatus>(source ? "loading" : "unavailable");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [nonce, setNonce] = useState(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    if (!source) {
      setStatus("unavailable");
      setThreads([]);
      return;
    }
    let current = true;
    setStatus("loading");
    setLoadError(null);
    // Through a resolved promise, so a source that throws as it is called (a
    // client built before the method existed) fails this load, not the page.
    Promise.resolve()
      .then(() => source.list())
      .then(
      (loaded) => {
        if (!current) return;
        if (loaded === null) {
          setThreads([]);
          setStatus("unsupported");
          return;
        }
        setThreads([...loaded].sort((a, b) => a.number - b.number));
        setStatus("ready");
      },
      (err: unknown) => {
        if (!current) return;
        setLoadError(reasonOf(err));
        setStatus("failed");
      },
    );
    return () => {
      current = false;
    };
  }, [source, surveyId, nonce]);

  /** Runs one write: busy while it is out, the thread it answers kept, a refusal said. */
  const write = useCallback(
    async <T,>(run: () => Promise<T>, what: string, keep: (result: T) => void): Promise<T | null> => {
      setBusy(true);
      setError(null);
      try {
        const result = await run();
        if (alive.current) keep(result);
        return result;
      } catch (err) {
        if (alive.current) setError(`${what} ${reasonOf(err)}`);
        return null;
      } finally {
        if (alive.current) setBusy(false);
      }
    },
    [],
  );

  const create = useCallback<PlanCommentsState["create"]>(
    (input) =>
      source
        ? write(() => source.create(input), "The comment could not be posted.", (t) => setThreads((l) => upsert(l, t)))
        : Promise.resolve(null),
    [source, write],
  );
  const reply = useCallback<PlanCommentsState["reply"]>(
    (threadId, body) =>
      source
        ? write(() => source.reply(threadId, body), "The reply could not be posted.", (t) => setThreads((l) => upsert(l, t)))
        : Promise.resolve(null),
    [source, write],
  );
  const update = useCallback<PlanCommentsState["update"]>(
    (id, patch) =>
      source
        ? write(
            () => source.update(id, patch),
            patch.status === "resolved"
              ? "The comment could not be resolved."
              : patch.status === "open"
                ? "The comment could not be reopened."
                : "The change could not be saved.",
            (t) => setThreads((l) => upsert(l, t)),
          )
        : Promise.resolve(null),
    [source, write],
  );
  const remove = useCallback<PlanCommentsState["remove"]>(
    async (id) => {
      if (!source) return false;
      const done = await write(
        () => source.remove(id).then(() => true),
        "The comment could not be deleted.",
        () =>
          setThreads((list) =>
            list
              // A thread goes with its replies; a reply leaves its thread.
              .filter((t) => t.id !== id)
              .map((t) => (t.replies.some((r) => r.id === id) ? { ...t, replies: t.replies.filter((r) => r.id !== id) } : t)),
          ),
      );
      return done === true;
    },
    [source, write],
  );

  const dismissError = useCallback(() => setError(null), []);
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { status, threads, loadError, error, busy, create, reply, update, remove, dismissError, reload };
}
